"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.FleetTable = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const routes_1 = require("../../../../util/routes");
const compliance_pool_types_1 = require("../../compliance-pool-types");
const common_styled_components_1 = require("../../../common-styled-components");
const react_1 = require("react");
const Modal_1 = require("../../common/Modal");
const useEdit_1 = __importDefault(require("../../../../hooks/useEdit"));
const amountUtils_1 = require("../../../../util/amountUtils");
const compliance_1 = require("../../../../util/compliance/compliance");
const isNullish_1 = require("../../../../util/isNullish");
const useSortedData_1 = require("../../../../hooks/useSortedData");
const NavigationContext_1 = require("../../../../contexts/NavigationContext");
const lodash_1 = require("lodash");
const ExportCsvButton_1 = require("../ExportCsvButton");
const tableColumns = (isInAdminTools) => [
    {
        key: "name",
        label: "Name",
        sortableKey: "name",
    },
    {
        key: "ships",
        label: "Ships",
    },
    {
        key: "intensity",
        label: "Intensity",
        sortableKey: "intensity.adjusted_intensity_gCO2e_per_MJ",
    },
    {
        key: "balance",
        label: "Balance",
        sortableKey: "intensity.adjusted_emission_balance_ton",
    },
    {
        key: "tools",
        label: "Tools",
    },
].filter((t) => t.key !== "tools" || isInAdminTools);
const CellContent = ({ isInAdminTools, fleet, contentKey, editFleet, compliance, removeFleet, }) => {
    switch (contentKey) {
        case "name":
            const link = isInAdminTools
                ? `${routes_1.routeNames.admin}/${routes_1.routeNames.fleetAdmin}/${fleet.id}`
                : `${routes_1.routeNames.fleet}/${fleet.id}`;
            return link ? ((0, jsx_runtime_1.jsx)(NavigationContext_1.LinkWithHistory, { to: link, children: (0, jsx_runtime_1.jsx)(common_styled_components_1.Bold, { children: fleet.name }) })) : ((0, jsx_runtime_1.jsx)("div", { children: fleet.name }));
        case "ships":
            return (0, jsx_runtime_1.jsx)("div", { children: fleet.ships.length });
        case "tools":
            return removeFleet && editFleet ? ((0, jsx_runtime_1.jsxs)("div", { children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.SmallButton, { onClick: () => removeFleet(fleet), children: "Remove" }), (0, jsx_runtime_1.jsx)(common_styled_components_1.SmallButton, { onClick: () => editFleet(fleet), children: "Edit" })] })) : ((0, jsx_runtime_1.jsx)("div", {}));
        case "balance":
            if (fleet.intensity && compliance) {
                return ((0, jsx_runtime_1.jsxs)("div", { style: { color: compliance_1.complianceOptions[compliance].color }, children: [fleet.intensity.adjusted_emission_balance_ton > 0 ? "+" : "", (0, amountUtils_1.formatNumber)(fleet.intensity.adjusted_emission_balance_ton, 2)] }));
            }
            else {
                return (0, jsx_runtime_1.jsx)("div", {});
            }
        case "intensity":
            if (fleet.intensity && compliance) {
                return ((0, jsx_runtime_1.jsxs)("div", { style: { color: compliance_1.complianceOptions[compliance].color }, children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.ComplianceIcon, { src: compliance_1.complianceOptions[compliance].icon }), (0, amountUtils_1.formatNumber)(fleet.intensity.adjusted_intensity_gCO2e_per_MJ, 2)] }));
            }
            else {
                return (0, jsx_runtime_1.jsx)("div", {});
            }
        default:
            return (0, jsx_runtime_1.jsx)("div", { children: fleet[contentKey] });
    }
};
const FleetTable = ({ fleets, isInAdminTools, refresh, }) => {
    const [editStatus, executeEdit] = (0, useEdit_1.default)("admin/fleet.json", refresh);
    const [modal, setModal] = (0, react_1.useState)({});
    const { sortedData, sortMethod, sortDirection, setSort } = (0, useSortedData_1.useSortedData)(fleets, "id", 1);
    const addFleet = () => {
        setModal({
            type: "add",
            value: undefined,
        });
    };
    const editFleet = (fleet) => {
        setModal({
            type: "edit",
            value: fleet,
        });
    };
    const removeFleet = (fleet) => {
        setModal({
            type: "remove",
            value: fleet,
        });
    };
    const submit = (type, value) => {
        setModal({});
        const fleetInput = (0, lodash_1.pick)(value, compliance_pool_types_1.fleetInputKeys);
        if (type === "add") {
            if ((0, compliance_pool_types_1.isValidFleetInput)(fleetInput)) {
                const data = {
                    ...fleetInput,
                    id: null,
                };
                executeEdit(data, "PUT");
            }
            else {
                alert("Empty name or fleet id");
            }
        }
        if (type === "edit") {
            if ((0, compliance_pool_types_1.isValidFleetInput)(fleetInput) && !(0, isNullish_1.isNullish)(value.id)) {
                executeEdit(fleetInput, "PUT");
            }
            else {
                alert("Empty name");
            }
        }
        if (type === "remove") {
            if (!(0, isNullish_1.isNullish)(value.id)) {
                const data = {
                    id: value.id,
                };
                executeEdit(data, "DELETE");
            }
        }
    };
    const columns = tableColumns(isInAdminTools);
    const csvColumns = columns
        .filter((column) => column.key !== "tools")
        .map((column) => ({
        header: column.label,
        getValue: (fleet) => {
            switch (column.key) {
                case "name":
                    return fleet.name;
                case "ships":
                    return fleet.ships.length;
                case "intensity":
                    return fleet.intensity
                        ? (0, amountUtils_1.formatNumber)(fleet.intensity.adjusted_intensity_gCO2e_per_MJ, 2) ?? ""
                        : "";
                case "balance":
                    return fleet.intensity
                        ? `${fleet.intensity.adjusted_emission_balance_ton > 0 ? "+" : ""}${(0, amountUtils_1.formatNumber)(fleet.intensity.adjusted_emission_balance_ton, 2) ?? ""}`
                        : "";
                default: {
                    const value = fleet[String(column.key)];
                    return typeof value === "string" || typeof value === "number"
                        ? value
                        : "";
                }
            }
        },
    }));
    return ((0, jsx_runtime_1.jsxs)("div", { children: [isInAdminTools && ((0, jsx_runtime_1.jsx)(Modal_1.Modal, { type: "fleet", isOpen: modal.type !== undefined, closeModal: () => setModal({}), modalType: modal.type, value: modal.value, submit: submit })), (0, jsx_runtime_1.jsx)(common_styled_components_1.TitleWithExtraContent, { titleLevel: "Section", extraContent: (0, jsx_runtime_1.jsxs)(common_styled_components_1.Row, { gap: 1, children: [isInAdminTools && (0, jsx_runtime_1.jsx)(common_styled_components_1.Button, { onClick: addFleet, children: "+ Add" }), (0, jsx_runtime_1.jsx)(ExportCsvButton_1.ExportCsvButton, { rows: sortedData, columns: csvColumns, tableName: "fleets" })] }), children: "Fleets" }), (0, jsx_runtime_1.jsxs)(common_styled_components_1.Table, { children: [(0, jsx_runtime_1.jsx)("thead", { children: (0, jsx_runtime_1.jsx)("tr", { children: columns.map((c) => ((0, jsx_runtime_1.jsx)(common_styled_components_1.SortableTH, { setSort: setSort, sortKey: c.sortableKey, sortDirection: sortMethod && sortMethod === c.sortableKey
                                    ? sortDirection
                                    : undefined, children: c.label }, c.key))) }) }), (0, jsx_runtime_1.jsx)("tbody", { children: sortedData.map((fleet) => {
                            const compliance = fleet.intensity
                                ? (0, compliance_1.getCompliance)(fleet.intensity.adjusted_emission_balance_ton)
                                : undefined;
                            return ((0, jsx_runtime_1.jsx)(common_styled_components_1.TR, { children: columns.map((c) => ((0, jsx_runtime_1.jsx)(common_styled_components_1.TD, { children: (0, jsx_runtime_1.jsx)(CellContent, { isInAdminTools: isInAdminTools, contentKey: c.key, fleet: fleet, removeFleet: isInAdminTools ? removeFleet : undefined, editFleet: isInAdminTools ? editFleet : undefined, compliance: compliance }) }, c.key))) }, fleet.id));
                        }) })] })] }));
};
exports.FleetTable = FleetTable;
